import { X } from "lucide-react";
import React, { ReactNode } from "react";

import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";

import { IgButton } from "./IgButton";
import { IgIconButton } from "./IgIconButton";
import { IgTooltip } from "./IgTooltip";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "&.MuiDialog-root": {
    marginLeft: "0 !important",
  },
}));

interface IgFullPageDialogProps extends Omit<
  DialogProps,
  "open" | "fullScreen" | "content"
> {
  showDialog: boolean;
  handleClose: (event?: any, reason?: any) => void;
  content: ReactNode;
  header?: ReactNode;
  noButtonText?: string | null;
  handleNoClick?: () => void;
  hideCancelButton?: boolean;
  yesButtonText?: string;
  handleYesClick?: () => void;
  yesButtonDisabled?: boolean;
  yesButtonDisabledTooltip?: string;
  hideActions?: boolean;
  hideYesButton?: boolean;
  shouldNotClose?: boolean;
  leftButtonHidden?: boolean;
  leftButtonText?: string;
  onLeftButtonClick?: () => void;
  leftSideNav?: ReactNode;
  disableContentPadding?: boolean;
}

export const IgFullPageDialog = ({
  showDialog,
  handleClose,
  content,
  header,
  noButtonText,
  handleNoClick,
  hideCancelButton = false,
  yesButtonText,
  handleYesClick,
  yesButtonDisabled = false,
  yesButtonDisabledTooltip,
  hideActions = false,
  hideYesButton = false,
  shouldNotClose,
  leftButtonHidden = true,
  leftButtonText,
  onLeftButtonClick,
  leftSideNav,
  disableContentPadding = false,
  ...rest
}: IgFullPageDialogProps) => {
  const theme = useTheme();
  const onYesClickPress = () => {
    if (handleYesClick) {
      handleYesClick();
    }
    if (!shouldNotClose) {
      handleClose();
    }
  };

  const onNoClickPress = () => {
    if (handleNoClick) {
      handleNoClick();
    } else {
      handleClose();
    }
  };

  return (
    <StyledDialog open={showDialog} onClose={handleClose} fullScreen {...rest}>
      {header && (
        <Box
          sx={{
            padding: "12px 16px",
            backgroundColor: theme.igBackgroundColors.baseLightMid,
            borderBottom: `1px solid ${theme.igBorders.core.default}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {header}
          {!hideCancelButton && (
            <IgIconButton onClick={handleClose} sx={{ flexShrink: 0, ml: 2 }}>
              <X size={24} />
            </IgIconButton>
          )}
        </Box>
      )}
      {!leftSideNav ? (
        <DialogContent
          sx={{
            padding: disableContentPadding ? 0 : 8,
            paddingBottom: hideActions ? undefined : 0,
            backgroundColor: theme.igBackgroundColors.baseLightLow,
          }}
        >
          {content}
        </DialogContent>
      ) : (
        <DialogContent
          sx={{
            padding: 0,
            paddingBottom: hideActions ? undefined : 0,
            backgroundColor: theme.igBackgroundColors.baseLightLow,
            overflow: "hidden",
          }}
        >
          <div
            style={{ display: "flex", flexDirection: "row", height: "100%" }}
          >
            {leftSideNav}
            <Box sx={{ flexGrow: 1, overflowY: "auto" }}>{content}</Box>
          </div>
        </DialogContent>
      )}
      {!hideActions && (
        <DialogActions
          sx={{
            padding: "16px 32px 16px 32px",
            backgroundColor: theme.palette.common.white,
            border: `1px solid ${theme.igBorders.core.default}`,
          }}
        >
          <Grid
            sx={{ width: "100%" }}
            container
            justifyContent={"space-between"}
            wrap={"nowrap"}
          >
            {!leftButtonHidden && (
              <Grid>
                <IgButton
                  variant={"outlined"}
                  color={"secondary"}
                  onClick={onLeftButtonClick}
                  sx={{ width: "max-content", paddingX: 8 }}
                >
                  {leftButtonText || "Back"}
                </IgButton>
              </Grid>
            )}
            <Grid container justifyContent="end" direction="row" size={12}>
              {noButtonText !== null && (
                <Grid marginRight={3}>
                  <IgButton
                    variant="contained"
                    color="secondary"
                    sx={{ minWidth: "112px" }}
                    onClick={onNoClickPress}
                  >
                    {noButtonText || "Cancel"}
                  </IgButton>
                </Grid>
              )}
              {!hideYesButton && (
                <Grid>
                  <IgTooltip title={yesButtonDisabledTooltip || ""}>
                    <IgButton
                      variant="contained"
                      color="primary"
                      sx={{ minWidth: "96px" }}
                      disabled={yesButtonDisabled}
                      onClick={onYesClickPress}
                    >
                      {yesButtonText || "Next"}
                    </IgButton>
                  </IgTooltip>
                </Grid>
              )}
            </Grid>
          </Grid>
        </DialogActions>
      )}
    </StyledDialog>
  );
};
